import React, { useState, useEffect } from 'react';
import MeetingsAPI, { ReviewablePendingBookDto } from '../../api/MeetingsAPI';
import { useTranslation } from 'react-i18next';
import Tab from '../Tab/Tab';
import PendingMeetingCard from './PendingMeetingCard';
import PendingRefundingCard from './PendingRefundingCard';
import { toastService } from '../../services/ToastService';

const BookingMeetingList: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const [pendingMeetings, setPendingMeetings] = useState<ReviewablePendingBookDto[]>([]);
  const [refundableMeetings, setRefundableMeetings] = useState<ReviewablePendingBookDto[]>([]);
  const [activeTab, setActiveTab] = useState("pending");
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    fetchMeetings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchMeetings = async () => {
    setIsLoading(true);
    try {
      // Get all meetings or filtered based on the active tab
      const response = await MeetingsAPI.pendings(activeTab === "refunds" ? "refunds" : "pending");
      
      if (activeTab === "refunds") {
        setRefundableMeetings(response.data.meetings);
      } else {
        setPendingMeetings(response.data.meetings);
      }
    } catch (error) {
      console.error('Failed to fetch meetings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBookingAction = async (id: number, action: 'accept' | 'reject') => {
    try {
      await MeetingsAPI[action](id);
      
      const updatedMeetings = pendingMeetings.filter(meeting => meeting.id !== id);
      setPendingMeetings(updatedMeetings);
      
      if (updatedMeetings.length === 0 && onClose) {
        onClose();
      }
      
      const event = new CustomEvent('pendingMeetingsUpdated');
      window.dispatchEvent(event);
    } catch (error) {
      console.error(`Error ${action}ing booking:`, error);
    }
  };

  const handleRefundProcessed = async (id: number) => {
    const updatedMeetings = refundableMeetings.filter(meeting => meeting.id !== id);
    setRefundableMeetings(updatedMeetings);
    
    if (updatedMeetings.length === 0 && onClose) {
      onClose();
    }
    
    const event = new CustomEvent('pendingMeetingsUpdated');
    window.dispatchEvent(event);
  };

  const handlePaymentStatusChange = async (id: number, paid: boolean) => {
    try {
      await MeetingsAPI.updatePaymentStatus(id, paid);
      
      const updatedMeetings = pendingMeetings.map(meeting => meeting.id === id ? { ...meeting, paid } : meeting);
      setPendingMeetings(updatedMeetings);
    } catch (error) {
      console.error('Error updating payment status:', error);
      toastService.showToast(t("booking.pending.payment_update_error") || "Failed to update payment status", "error");
    }
  };

  return (
    <div className="p-1 space-y-5">
      <div className="flex border-b border-gray-200">
        <Tab 
          label={t("booking.pending.tab_pending") || "Pending Bookings"} 
          isActive={activeTab === "pending"} 
          fn={() => setActiveTab("pending")}
        />
        <Tab 
          label={t("booking.pending.tab_refunds") || "Pending Refunds"} 
          isActive={activeTab === "refunds"} 
          fn={() => setActiveTab("refunds")}
        />
      </div>
      
      <div className="mt-4 space-y-4">
        {activeTab === "pending" && (
          isLoading ? (
            <div className="text-center py-4">{t("global.loading") || "Loading..."}</div>
          ) : pendingMeetings.length === 0 ? (
            <div className="text-center py-4">{t("booking.pending.no_pending") || "No pending bookings"}</div>
          ) : (
            pendingMeetings.map((meeting, index) => (
              <PendingMeetingCard 
                key={index} 
                meeting={meeting} 
                onAction={handleBookingAction} 
                onPaymentStatusChange={handlePaymentStatusChange}
              />
            ))
          )
        )}
        
        {activeTab === "refunds" && (
          isLoading ? (
            <div className="text-center py-4">{t("global.loading") || "Loading..."}</div>
          ) : refundableMeetings.length === 0 ? (
            <div className="text-center py-4">{t("booking.pending.no_refunds") || "No pending refunds"}</div>
          ) : (
            refundableMeetings.map((meeting, index) => (
              <PendingRefundingCard key={index} meeting={meeting} onRefundProcessed={handleRefundProcessed} />
            ))
          )
        )}
      </div>
    </div>
  );
};

export default BookingMeetingList;